import {ReportDefinitionV1} from '../contracts/report-definition.types';
import {ReportTaxonomy} from '../contracts/report-taxonomy.types';
import {GroupingToken} from '../ReportTable/useClientGrouping';
import {ReportDataResult} from './reportRowModel';
import {FormatLabel} from '../projectStatusLabels';
import {SavedReportRef} from './savedReportInput';

/** Where a report sits in the publish lifecycle, as report-service reports it. */
export type ReportDraftState = 'PUBLISHED' | 'DRAFT' | 'UNPUBLISHED';

/** A saved report as the builder needs it on re-open: the definition to render, the provenance that
 * decides whether the surface shows an AI banner and what prompt it quotes, the lifecycle state the
 * banner and the Discard action key off, and the sharing flag every update has to echo back. */
export interface LoadedReport {
	definition: ReportDefinitionV1;
	source: 'AI' | 'STANDARD';
	promptText: string | null;
	/** Whether this report has been hand-edited (conditions or reporting period) since it was generated
	 * from its prompt. Drives the AI banner's second state. False when the stored record carries no
	 * boolean: a report predating the flag has no known manual edit, which is what the original wording
	 * already claims. */
	manuallyEdited: boolean;
	/** What the logged-in person may do with this report. Resolved by the backend, which is the same
	 * place the rule is enforced, so the surface never has to re-derive it. */
	viewerPermission: ViewerPermission;
	/** The stored report's lifecycle state, narrowed to the known union. Null when the stored record
	 * carries no state or one this client does not recognise — consumers must read a null as "no
	 * reader isolation proven" rather than inventing one. Without this a re-opened published report
	 * would start as UNPUBLISHED, so the banner would claim it had never been published and Discard
	 * would be permanently disabled. */
	draftState: ReportDraftState | null;
	/** When the report was last published, or null when it never was. Drives view mode's
	 * "Published {date}" line and `beginEdit`'s conservative fallback. */
	publishedAt: string | null;
	/** Whether the stored report is private to its owner. Read back rather than guessed: an update
	 * overwrites this field unconditionally, so a guessed value would silently flip a report between
	 * private and shared. Null when the stored record does not carry a boolean, in which case there is
	 * no truthful value to echo and the caller must not invent one. */
	privateAccess: boolean | null;
	/** Whether discarding the currently open draft would also revert the report's filter. False
	 * (including when no draft is open, or the stored record predates this field) means discard leaves
	 * filter live, exactly as today. Drives whether the discard-confirmation copy mentions filter. */
	draftFilterWillRevert: boolean;
}

/** What every lifecycle call resolves with. `locked` is true when another user holds the edit lock,
 * in which case nothing changed server-side and the other two fields are null. */
export interface ReportLifecycleState {
	draftState: ReportDraftState | null;
	publishedAt: string | null;
	locked: boolean;
	/** See `LoadedReport.draftFilterWillRevert` — same meaning, refreshed by whichever lifecycle call
	 * (enterDraft/publish/discard) this state came from. */
	draftFilterWillRevert: boolean;
}

export type ViewerPermission = 'OWNER' | 'EDITOR' | 'VIEWER';

/** Who holds a report's edit lock. All fields but `locked` are null when it is free. */
export interface LockStatus {
	locked: boolean;
	/** Whether the acting viewer is the holder, or null when the server could not tell — resolving it
	 * means a user-service lookup, and a transient failure there must not be read as "not the viewer".
	 * That answer is what decides whether a tab holding a live token concludes it lost the lock, so the
	 * three states are kept distinct rather than collapsed into a boolean. */
	heldByViewer: boolean | null;
	holder: {id: string; fullName: string; profilePictureId: string | null} | null;
	lockedAt: string | null;
	/** When the lock lapses if it is not renewed. Carried because it is part of the lock as
	 * report-service reports it, and because a lock read is the only place the client can learn it; no
	 * consumer reads it today. Nothing may branch on it: the client's clock is not the server's, so a
	 * client-side comparison against it would conclude a live lock had lapsed (or the reverse) on any
	 * clock skew. Liveness is decided server-side, and the client learns of it through a refused
	 * heartbeat or write. */
	expiresAt: string | null;
}

/** A claim attempt. `token` is null when someone else holds the lock, in which case `status`
 * names them. Resolves rather than rejects on a lost race — a contended lock is an expected
 * outcome, not a transport failure. */
export interface LockClaim {
	token: string | null;
	status: LockStatus;
}

/** Thrown by `exportCsv` when the backend refuses the export because the report has more rows than it
 * will order. Part of the interface rather than of one implementation, so the surface can word that case
 * differently without importing the HTTP transport, and any other implementation can raise it too.
 *
 * A type rather than a message or a status code on a generic error: nothing downstream should branch on
 * a string, because a copy change would silently fold this case back into the generic failure — which
 * would tell the user to retry something that cannot succeed. */
export class ReportExportTooLargeError extends Error {
	constructor() {
		super('the report has too many rows to export');
		this.name = 'ReportExportTooLargeError';
	}
}

/** Which refusal a 403 on a report definition was: the report's type needs a financial permission
 * the reader lacks (an administrator can help), or the report was not shared with them (its owner
 * can). The server names it; the words for each live with the page that shows them. */
export type ReportRefusalReason = 'REPORT_TYPE_PERMISSION' | 'NOT_SHARED';

/** A data fetch that reached the server and came back non-2xx, carrying the status so callers can
 * tell an access refusal apart from a failure worth retrying. A network failure never produces this
 * — it rejects with a plain `Error`, which correctly reads as "no status, treat as retryable".
 *
 * A type with a numeric field rather than a status parsed back out of a message string: nothing
 * downstream should branch on prose, because a reworded message would silently fold a refusal back
 * into the generic failure and tell the user to retry something that cannot succeed. */
export class ReportDataFetchError extends Error {
	readonly status: number;
	/** Set only when the server named which refusal a 403 was. Absent on every other failure, and on
	 * a 403 whose body could not be read - which the page reads as not shared, the common case. */
	readonly reason?: ReportRefusalReason;

	constructor(status: number, message: string, reason?: ReportRefusalReason) {
		super(message);
		this.name = 'ReportDataFetchError';
		this.status = status;
		this.reason = reason;
	}
}

/** True only for a refusal the viewer cannot retry away: the server understood the request, the
 * session is valid, and this viewer is still not allowed the data. Anything else — 5xx, a timeout,
 * an offline browser — stays retryable. */
export function isReportDataAccessDenied(error: unknown): boolean {
	return error instanceof ReportDataFetchError && error.status === 403;
}

export interface ReportDataSource {
	/** Runs the report and maps its rows. `taxonomy` types the cells the definition's grain declares
	 * as durations, dates or percentages - none of which a value identifies itself as on the wire.
	 * It is optional because it resolves asynchronously: without it the rows still arrive, typed by
	 * shape alone, and the caller re-runs the fetch when it lands.
	 *
	 * `formatLabel` resolves the display labels a few cells carry (a project's status and stage) to
	 * the reader's language. Passed per call rather than held on the source because the source is a
	 * module-level singleton constructed outside React, and rebuilding it per render to inject an
	 * intl would change its identity on every render - which the surface treats as a new source and
	 * refetches for. Omitting it maps those labels in English.
	 *
	 * `grouping` is the active client-side grouping, passed through so an implementation can fetch
	 * whatever a token needs beyond `definition.fields` (see `HttpReportDataSource.fetchFieldsFor`) —
	 * a project-status custom-field token's cell has no value on the row otherwise. Optional: an
	 * implementation with nothing extra to fetch for any token (or a caller with no live grouping to
	 * report, e.g. before it exists) may omit it. */
	fetchRows(
		definition: ReportDefinitionV1,
		page: {offset: number; limit: number},
		taxonomy?: ReportTaxonomy | null,
		formatLabel?: FormatLabel,
		grouping?: GroupingToken[]
	): Promise<ReportDataResult>;
	/** Persists the definition as a first-class entry in the Reports list and resolves with the
	 * reference to it. Pass `saved` as null on the first save to create the report; pass the
	 * reference an earlier save resolved with to update that same report rather than create a
	 * duplicate. `promptText` is the request the report was generated from and is recorded only on
	 * the create; an update leaves the stored value alone. Rejects when the save fails — the caller
	 * must handle the rejection, because the report is then still unsaved and the surface has to say
	 * so.
	 *
	 * `lockToken` is this tab's edit-lock token when it holds one, and is presented on every write
	 * report-service gates. It refuses the write once that token no longer names a live lock this caller
	 * holds — the check that stops a tab whose lock lapsed while it slept from overwriting whatever was
	 * published in the meantime. Omitted when no lock is held: an absent token means "does not
	 * participate in the lock protocol", which is gated only on not colliding with someone else's live
	 * lock, so passing an empty string instead would turn every unlocked save into a conflict. */
	saveDefinition(
		definition: ReportDefinitionV1,
		saved: SavedReportRef | null,
		promptText: string | null,
		manuallyEdited: boolean,
		lockToken?: string | null
	): Promise<SavedReportRef>;
	/** Loads a saved report's definition, plus its provenance, so the builder can re-open it. Rejects
	 * when the report cannot be read (missing, not permitted, or transport failure) — the caller has no
	 * definition to render and must say so rather than showing an empty builder. */
	loadDefinition(savedReportId: string): Promise<LoadedReport>;
	/** Exports the definition's full (unpaginated) result set as a CSV, honouring the active
	 * client-side grouping's sort order, and hands the file to the browser's download flow.
	 * `hiddenColumnPaths` are the columns switched off in ColumnManager, which the file leaves out so it
	 * matches the table on screen; hiding is live table state, so it cannot be read off the definition.
	 * Rejects when the export fails — the caller must handle the rejection, because no file was
	 * ever handed to the browser. Rejects with `ReportExportTooLargeError` when the backend refused the
	 * export outright for having too many rows, which is the one failure a retry cannot get past. */
	exportCsv(
		definition: ReportDefinitionV1,
		grouping: GroupingToken[],
		hiddenColumnPaths?: readonly string[],
		taxonomy?: ReportTaxonomy | null
	): Promise<void>;
	/** Opens an edit session, so readers keep executing the published version while the working copy
	 * diverges. A report with no published version cannot open one, and resolves `UNPUBLISHED`.
	 * Rejects on transport failure. */
	enterDraft(savedReportId: string): Promise<ReportLifecycleState>;
	/** Makes the working copy the version readers execute and closes the edit session. Rejects on
	 * transport failure. `lockToken` as on `saveDefinition`. */
	publish(savedReportId: string, lockToken?: string | null): Promise<ReportLifecycleState>;
	/** Drops the edit session, reverting the drafted columns, name, period and grouping to the
	 * baseline captured when the session opened. Rejects on transport failure. `lockToken` as on
	 * `saveDefinition`. */
	discard(savedReportId: string, lockToken?: string | null): Promise<ReportLifecycleState>;
	/** Reads who currently holds the edit lock, without attempting to claim it. Used to render the
	 * banner on load and to reconcile after a pushed socket event. Rejects on transport failure. */
	lockStatus(savedReportId: string): Promise<LockStatus>;
	/** Claims the edit lock. Resolves with a null token (and the current holder in `status`) when
	 * someone else already holds it — a contended lock is a normal outcome, not a rejection. */
	acquireLock(savedReportId: string): Promise<LockClaim>;
	/** Extends the lock this tab holds. A returned token that differs from `token` means the session
	 * was broken (it lapsed and someone else has since claimed it) — the caller must never
	 * adopt the new token, only detect the mismatch. */
	heartbeatLock(savedReportId: string, token: string): Promise<LockClaim>;
	/** Releases the lock this tab holds. Rejects on transport failure. */
	releaseLock(savedReportId: string, token: string): Promise<void>;
	/** Persists a row's favorite state via the same task-favourite mechanism used elsewhere in the
	 * app (My Work, etc.) — the report builder does not have its own favourite concept. Rejects on
	 * transport failure; the caller is responsible for reverting any optimistic UI update. */
	toggleFavorite(taskId: string, favoured: boolean): Promise<void>;
}
